/**
 * H3 van blok H (Frank, 18 oktober 2026): het eerste-startscherm.
 *
 * Is er op deze installatie nog geen basismap, dan vraagt NovaMund erom, met
 * de echte mapkiezer van de Mac (`window.novamundDesktop.kiesMap`, uit de
 * preload van de schil).
 *
 * ALLEEN IN DE MAC-APP (Frank, 18 oktober 2026, na de proefrevisie blok-h): op
 * de telefoon verscheen het scherm ook, met een tekstveld voor een pad op de
 * Mac. Waar de kiezer ontbreekt (PWA, browser) toont het zich niet, en daarom
 * is er ook geen tekstveld meer.
 *
 * ALLEEN EEN EIGENAAR ZIET HET SCHERM, en dat zegt de backend (`mag_zetten`),
 * niet deze component. Een lid kan de basismap niet zetten en hoort dus ook niet
 * gevraagd te worden.
 *
 * "LATER" GELDT TOT DE VOLGENDE START. Het scherm komt terug zolang er geen
 * basismap is; dat is de bedoeling van een eerste-startscherm. Bewaard in
 * `sessionStorage`, in een try, want die kan ontbreken.
 *
 * F3a INSTALLATIEPROFIEL (Frank, 20 oktober 2026). Hetzelfde scherm vraagt nu ook
 * de organisatienaam, de taal en het Nextcloud-adres als data-thuis. Ligt er een
 * `novamund-profiel.json` in de datamap, dan VULT dat de velden voor; de eigenaar
 * bevestigt altijd zelf, er wordt niets stil gezet. Bestaat de basismap uit het
 * profiel niet op deze Mac, dan staat dat er en kiest de eigenaar met de
 * mapkiezer. Alleen voor nieuwe installaties: het scherm verschijnt nog steeds
 * alleen zolang er geen basismap is. Opslaan loopt via de bestaande routes, en de
 * basismap als laatste, want die sluit het scherm.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { useBranding } from "@/contexts/BrandingContext";

export const LATER = "novamund_basismap_later";

/** Wat het scherm invult: het profiel gaat voor, anders wat er al staat. PUUR.
 *  Een basismap uit het profiel alleen als hij op deze Mac bestaat (keuze 4). */
export function beginwaarden(profiel, organisatie, taal) {
  const v = (profiel && typeof profiel.voorstel === "object" && profiel.voorstel) || {};
  return {
    naam: v.naam || organisatie?.naam || "",
    taal: v.taal === "en" || v.taal === "nl" ? v.taal : taal === "en" ? "en" : "nl",
    data_thuis: v.data_thuis || organisatie?.data_thuis || "",
    pad: v.basismap && profiel.basismap_bestaat === true ? v.basismap : "",
    ontbrekendePad: v.basismap && profiel.basismap_bestaat === false ? v.basismap : "",
    huisstijl: huisstijlUit(v),
    persona: personaUit(v),
  };
}

/** F3b: de huisstijl uit het profiel, in de vorm van `/api/branding`; alleen wat er
 *  staat, zodat een leeg veld de standaard van NovaMund houdt. De naam gaat via de
 *  organisatie (een veld). PUUR. */
export function huisstijlUit(voorstel) {
  const v = voorstel || {};
  const uit = {};
  if (v.accentkleur) uit.accent_color = v.accentkleur;
  if (v.toon) uit.tone_of_voice = v.toon;
  if (v.woordkeuze) uit.terminology = v.woordkeuze;
  if (v.logo) uit.logo_data_uri = v.logo;
  return uit;
}

/** POST P: de persona uit het profiel, in de vorm van `/api/branding/persona`; alleen wat er staat. PUUR. */
export function personaUit(voorstel) {
  const v = voorstel || {};
  const uit = {};
  if (v.persona_en) uit.persona_en = v.persona_en;
  if (v.persona_nl) uit.persona_nl = v.persona_nl;
  if (v.kosmische_laag) uit.kosmisch = v.kosmische_laag === "aan";
  return uit;
}

/** Of het scherm zich toont. PUUR. Alleen in de Mac-app (`opDeMac`). */
export function moetTonen(stand, later, opDeMac) {
  return Boolean(opDeMac && stand && !stand.basismap && stand.mag_zetten === true && !later);
}

/** De mapkiezer van de Mac, of null buiten de app. */
export function desktopKiezer(venster = typeof window === "undefined" ? undefined : window) {
  const kies = venster?.novamundDesktop?.kiesMap;
  return typeof kies === "function" ? kies : null;
}

function leesLater() {
  try {
    return window.sessionStorage.getItem(LATER) === "1";
  } catch {
    return false;
  }
}

function zetLater() {
  try {
    window.sessionStorage.setItem(LATER, "1");
  } catch {
    /* zonder opslag komt het scherm bij de volgende keer gewoon terug */
  }
}

async function leesOfNiets(url) {
  try {
    return (await axios.get(url))?.data || null;
  } catch {
    return null;
  }
}

export default function EersteStart() {
  const { t, taal: appTaal, zetTaal } = useTaal();
  const { refreshBranding } = useBranding();
  const [open, setOpen] = useState(false);
  const [pad, setPad] = useState("");
  const [naam, setNaam] = useState("");
  const [taal, setTaal] = useState("nl");
  const [dataThuis, setDataThuis] = useState("");
  const [ontbrekendePad, setOntbrekendePad] = useState("");
  const [huisstijl, setHuisstijl] = useState({});
  const [persona, setPersona] = useState({});
  const [beginNaam, setBeginNaam] = useState("");
  const [fout, setFout] = useState("");
  const [bezig, setBezig] = useState(false);
  const kiezer = desktopKiezer();

  useEffect(() => {
    let weg = false;
    (async () => {
      // EERST DE ORGANISATIE, gemeten op 20 oktober 2026 op een verse datamap: daar
      // wijst pas die aanroep de eerste gebruiker als eigenaar aan. Daarvoor zegt de
      // basismap `mag_zetten: false`, en dan bleef het scherm op de eerste start weg.
      const organisatie = await leesOfNiets(`${API}/organisatie`);
      const stand = await leesOfNiets(`${API}/settings/basismap`);
      if (weg || !moetTonen(stand, leesLater(), desktopKiezer() !== null)) return;
      const profiel = await leesOfNiets(`${API}/settings/profiel`);
      if (weg) return;
      const b = beginwaarden(profiel, organisatie, appTaal);
      setNaam(b.naam);
      setBeginNaam(organisatie?.naam || "");
      setTaal(b.taal);
      setDataThuis(b.data_thuis);
      setPad(b.pad);
      setOntbrekendePad(b.ontbrekendePad);
      setHuisstijl(b.huisstijl);
      setPersona(b.persona || {});
      setOpen(true);
    })();
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kies = async () => {
    const gekozen = await kiezer();
    if (gekozen) { setPad(gekozen); setFout(""); setOntbrekendePad(""); }
  };

  const bewaar = async () => {
    setBezig(true);
    setFout("");
    // Eerst naam, adres en taal; de basismap als laatste, want die sluit het scherm.
    // Valt een van de eerste drie om, dan blijft het scherm open en is de basismap
    // niet gezet, zodat het scherm bij de volgende start terugkomt.
    try {
      if (naam.trim() && naam.trim() !== beginNaam) {
        await axios.put(`${API}/organisatie`, { naam: naam.trim() });
      }
      if (dataThuis.trim()) {
        await axios.put(`${API}/organisatie/data-thuis`, { adres: dataThuis.trim() });
      }
      if (Object.keys(huisstijl).length) {
        await axios.put(`${API}/branding`, huisstijl);
      }
      if (Object.keys(persona).length) {
        await axios.put(`${API}/branding/persona`, persona);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setFout(typeof detail === "string" ? detail : t("Dat lukte niet. Er is niets gewijzigd."));
      setBezig(false);
      return;
    }
    // De zijbalk las de huisstijl bij het laden van de app, voor dit scherm. Zonder
    // verversen bleef daar "Novamund" staan tot iemand in Merk opsloeg (glasronde 0.9.180).
    await refreshBranding();
    if (taal !== appTaal) await zetTaal(taal);
    try {
      await axios.put(`${API}/settings/basismap`, { basismap: pad.trim() });
      setOpen(false);
    } catch (e) {
      setFout(e?.response?.status === 403
        ? t("Alleen een eigenaar kan de basismap kiezen.")
        : t("Dit is geen bestaande map met een volledig pad."));
    } finally {
      setBezig(false);
    }
  };

  const later = () => { zetLater(); setOpen(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) later(); }}>
      <DialogContent data-testid="eerste-start">
        <DialogHeader>
          <DialogTitle>{t("Kies je basismap")}</DialogTitle>
          <DialogDescription>
            {t("De basismap is de map waarin je vault staat, bijvoorbeeld Plexaris Brain. Kennisbanken onthouden hun map ten opzichte van deze map, zodat ze ook werken als de vault op een andere plek staat.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={kies} data-testid="eerste-start-kies">
              {t("Kies map...")}
            </Button>
            <span className="text-sm font-mono break-all min-w-0" data-testid="eerste-start-pad">{pad}</span>
          </div>
          {ontbrekendePad && (
            <p className="text-sm text-muted-foreground break-words" data-testid="eerste-start-pad-ontbreekt">
              {t("De map uit het profiel bestaat niet op deze Mac: {pad}. Kies een map.", { pad: ontbrekendePad })}
            </p>
          )}
          <label className="block space-y-1">
            <span className="text-sm font-medium">{t("Naam van de organisatie")}</span>
            <Input value={naam} onChange={(e) => setNaam(e.target.value)} data-testid="eerste-start-naam" />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">{t("Taal")}</span>
            <select value={taal} onChange={(e) => setTaal(e.target.value)} data-testid="eerste-start-taal"
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm">
              <option value="nl">Nederlands</option>
              <option value="en">English</option>
            </select>
          </label>
          {Object.keys(huisstijl).length > 0 && (
            <div className="space-y-1 rounded-md border p-2 text-sm" data-testid="eerste-start-huisstijl">
              <p className="font-medium">{t("Huisstijl uit het profiel")}</p>
              {huisstijl.accent_color && (
                <p className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 rounded border" style={{ background: huisstijl.accent_color }} />
                  {t("Accentkleur")} {huisstijl.accent_color}
                </p>
              )}
              {huisstijl.tone_of_voice && <p className="break-words">{t("Toon")}: {huisstijl.tone_of_voice}</p>}
              {huisstijl.terminology && <p className="break-words">{t("Woordkeus")}: {huisstijl.terminology}</p>}
              {huisstijl.logo_data_uri && (
                <p className="flex items-center gap-2">
                  <img src={huisstijl.logo_data_uri} alt="" className="h-6 w-6 object-contain" />
                  {t("Logo, naast het NovaMund-beeldmerk")}
                </p>
              )}
              <p className="text-xs text-muted-foreground">{t("Later te wijzigen in Instellingen > Merk.")}</p>
            </div>
          )}
          <label className="block space-y-1">
            <span className="text-sm font-medium">{t("Nextcloud-adres (data-thuis)")}</span>
            <Input value={dataThuis} onChange={(e) => setDataThuis(e.target.value)} placeholder="https://"
                   data-testid="eerste-start-data-thuis" />
          </label>
          {fout && <p className="text-sm text-destructive" role="alert">{fout}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={later} data-testid="eerste-start-later">{t("Later")}</Button>
          <Button type="button" onClick={bewaar} disabled={!pad.trim() || bezig} data-testid="eerste-start-bewaar">
            {t("Opslaan")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
